/**
 * pushAsk: when Karr asks a phone to turn its notifications on (webpushask).
 *
 * Founder, 4 Oct 2026, on the switch in Settings: *"this should automatically
 * come as a popup frequently until it is turned on"*. It came on every open of
 * Karr on a phone, and that became the complaint (TODO 22 Oct 2026, item 7:
 * "the webpush ask pops up on every open"). So the popup asks once per phone
 * and remembers the answer on the phone with its date:
 *
 *   - "Later": asked again after LATER_DAYS;
 *   - "Don't ask again": never asked by the popup again;
 *   - turned on: nothing to ask.
 *
 * The switch in Settings, Notifications and the fill rail row stay, so a
 * person who said no can still turn alerts on. The phone's own Allow box can
 * only follow a tap (Android and iPhone refuse it otherwise), so the popup is
 * Karr's card with a Turn on button.
 *
 * @since webpushask
 */
import type { PushState } from './webPush';

/** A "Later" holds this long before the popup asks again. */
export const LATER_DAYS = 30;
const DAY_MS = 24 * 60 * 60_000;
/** Let the page draw first: a card over a blank screen reads as a crash. */
export const ASK_DELAY_MS = 3_000;
/** Where this phone keeps its answer (per device, not per person). */
export const PUSH_ANSWER_KEY = 'karr_push_ask';

/** What this phone said to the popup, and when. */
export interface PushAnswer { answer: 'later' | 'no'; at: number }

/** States the popup has something to do about. */
const ASKABLE: ReadonlySet<PushState | 'checking'> = new Set<PushState | 'checking'>(['off', 'denied', 'home-screen', 'ios-update']);

export function shouldAskPush(i: {
    phone: boolean;
    state: PushState | 'checking';
    /** This phone's last answer to the popup, or null when it was never asked. */
    answered: PushAnswer | null;
    now: number;
}): boolean {
    if (!i.phone || !ASKABLE.has(i.state)) return false;
    if (!i.answered) return true;
    if (i.answered.answer === 'no') return false;
    return i.now - i.answered.at >= LATER_DAYS * DAY_MS;
}

/** The answer this phone gave, or null (never asked, unreadable or blocked storage). */
export function readPushAnswer(): PushAnswer | null {
    try {
        const v = JSON.parse(localStorage.getItem(PUSH_ANSWER_KEY) ?? 'null') as Partial<PushAnswer> | null;
        if (v && (v.answer === 'later' || v.answer === 'no') && typeof v.at === 'number') return { answer: v.answer, at: v.at };
    } catch { /* storage off: asked again next open, never a crash */ }
    return null;
}

export function savePushAnswer(answer: PushAnswer['answer'], now = Date.now()): PushAnswer {
    const a = { answer, at: now };
    try { localStorage.setItem(PUSH_ANSWER_KEY, JSON.stringify(a)); } catch { /* storage off */ }
    return a;
}

/** Said when the state changes anywhere, so the popup and the rail row follow. */
export const PUSH_CHANGED_EVENT = 'karr:push-changed';
